import { Injectable } from '@angular/core'
import { GbApiService } from 'libs/shared/data-access-core/src/lib/+state/gb-api.service'
import { Observable } from 'rxjs'
import {
  CountdownWidgetModel,
  ImageWidgetModel,
  TextWidgetModel,
  SmartTextWidgetModel,
  TotalRaisedWidgetModel,
  TeamScoringWidgetModel,
  PowerPledgeWidgetModel,
  NotificationWidgetModel,
  ItemTableWidgetModel,
  RaffleWinnersWidgetModel,
  HonourRollWidgetModel,
  ThermometerWidgetModel,
  RaffleDrawWidgetModel,
  QrCodeWidgetModel,
  VideoWidgetModel,
  HonourRollActivity,
} from './presenter-widgets.models'
import { GbPaginatedResponse } from '@galabid/gb-dashboard/core/data-access'

@Injectable({
  providedIn: 'root',
})
export class PresenterWidgetsApiService extends GbApiService {
  // Countdown Widgets
  getCountdownWidgets(urlName: string): Observable<CountdownWidgetModel[]> {
    return this.get<CountdownWidgetModel[]>(`auctions/${urlName}/countdown-widgets/`)
  }

  createCountdownWidget(
    urlName: string,
    countdownWidget: Omit<CountdownWidgetModel, 'uuid'>,
  ): Observable<CountdownWidgetModel> {
    const url = `auctions/${urlName}/countdown-widgets/`
    return this.post<CountdownWidgetModel>(url, countdownWidget)
  }

  updateCountdownWidget(
    urlName: string,
    countdownWidget: Partial<CountdownWidgetModel> & Pick<CountdownWidgetModel, 'uuid'>,
  ): Observable<CountdownWidgetModel> {
    const url = `auctions/${urlName}/countdown-widgets/${countdownWidget.uuid}/`
    return this.patch<CountdownWidgetModel>(url, countdownWidget)
  }

  deleteCountdownWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/countdown-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Image Widgets
  getImageWidgets(urlName: string): Observable<ImageWidgetModel[]> {
    return this.get<ImageWidgetModel[]>(`auctions/${urlName}/image-widgets/`)
  }

  createImageWidget(urlName: string, imageWidget: Omit<ImageWidgetModel, 'uuid'>): Observable<ImageWidgetModel> {
    const url = `auctions/${urlName}/image-widgets/`
    return this.post<ImageWidgetModel>(url, imageWidget)
  }

  updateImageWidget(
    urlName: string,
    imageWidget: Partial<ImageWidgetModel> & Pick<ImageWidgetModel, 'uuid'>,
  ): Observable<ImageWidgetModel> {
    const url = `auctions/${urlName}/image-widgets/${imageWidget.uuid}/`
    return this.patch<ImageWidgetModel>(url, imageWidget)
  }

  deleteImageWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/image-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Text Widgets
  getTextWidgets(urlName: string): Observable<TextWidgetModel[]> {
    return this.get<TextWidgetModel[]>(`auctions/${urlName}/text-widgets/`)
  }

  createTextWidget(urlName: string, textWidget: Omit<TextWidgetModel, 'uuid'>): Observable<TextWidgetModel> {
    const url = `auctions/${urlName}/text-widgets/`
    return this.post<TextWidgetModel>(url, textWidget)
  }

  updateTextWidget(
    urlName: string,
    textWidget: Partial<TextWidgetModel> & Pick<TextWidgetModel, 'uuid'>,
  ): Observable<TextWidgetModel> {
    const url = `auctions/${urlName}/text-widgets/${textWidget.uuid}/`
    return this.patch<TextWidgetModel>(url, textWidget)
  }

  deleteTextWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/text-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Smart Text Widgets
  getSmartTextWidgets(urlName: string): Observable<SmartTextWidgetModel[]> {
    return this.get<SmartTextWidgetModel[]>(`auctions/${urlName}/smart-text-widgets/`)
  }

  createSmartTextWidget(
    urlName: string,
    smartTextWidget: Omit<SmartTextWidgetModel, 'uuid'>,
  ): Observable<SmartTextWidgetModel> {
    const url = `auctions/${urlName}/smart-text-widgets/`
    return this.post<SmartTextWidgetModel>(url, smartTextWidget)
  }

  updateSmartTextWidget(
    urlName: string,
    smartTextWidget: Partial<SmartTextWidgetModel> & Pick<SmartTextWidgetModel, 'uuid'>,
  ): Observable<SmartTextWidgetModel> {
    const url = `auctions/${urlName}/smart-text-widgets/${smartTextWidget.uuid}/`
    return this.patch<SmartTextWidgetModel>(url, smartTextWidget)
  }

  deleteSmartTextWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/smart-text-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Total Raised Widgets
  getTotalRaisedWidgets(urlName: string): Observable<TotalRaisedWidgetModel[]> {
    return this.get<TotalRaisedWidgetModel[]>(`auctions/${urlName}/total-raised-widgets/`)
  }

  createTotalRaisedWidget(
    urlName: string,
    totalRaisedWidget: Omit<TotalRaisedWidgetModel, 'uuid'>,
  ): Observable<TotalRaisedWidgetModel> {
    const url = `auctions/${urlName}/total-raised-widgets/`
    return this.post<TotalRaisedWidgetModel>(url, totalRaisedWidget)
  }

  updateTotalRaisedWidget(
    urlName: string,
    totalRaisedWidget: Partial<TotalRaisedWidgetModel> & Pick<TotalRaisedWidgetModel, 'uuid'>,
  ): Observable<TotalRaisedWidgetModel> {
    const url = `auctions/${urlName}/total-raised-widgets/${totalRaisedWidget.uuid}/`
    return this.patch<TotalRaisedWidgetModel>(url, totalRaisedWidget)
  }

  deleteTotalRaisedWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/total-raised-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Team Scoring Widgets
  getTeamScoringWidgets(urlName: string): Observable<TeamScoringWidgetModel[]> {
    return this.get<TeamScoringWidgetModel[]>(`auctions/${urlName}/team-scoring-widgets/`)
  }

  createTeamScoringWidget(
    urlName: string,
    teamScoringWidget: Omit<TeamScoringWidgetModel, 'uuid'>,
  ): Observable<TeamScoringWidgetModel> {
    const url = `auctions/${urlName}/team-scoring-widgets/`
    return this.post<TeamScoringWidgetModel>(url, teamScoringWidget)
  }

  updateTeamScoringWidget(
    urlName: string,
    teamScoringWidget: Partial<TeamScoringWidgetModel> & Pick<TeamScoringWidgetModel, 'uuid'>,
  ): Observable<TeamScoringWidgetModel> {
    const url = `auctions/${urlName}/team-scoring-widgets/${teamScoringWidget.uuid}/`
    return this.patch<TeamScoringWidgetModel>(url, teamScoringWidget)
  }

  deleteTeamScoringWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/team-scoring-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Power Pledge Widgets
  getPowerPledgeWidgets(urlName: string): Observable<PowerPledgeWidgetModel[]> {
    return this.get<PowerPledgeWidgetModel[]>(`auctions/${urlName}/power-pledge-widget/`)
  }

  createPowerPledgeWidget(
    urlName: string,
    powerPledgeWidget: Omit<PowerPledgeWidgetModel, 'uuid'>,
  ): Observable<PowerPledgeWidgetModel> {
    const url = `auctions/${urlName}/power-pledge-widget/`
    return this.post<PowerPledgeWidgetModel>(url, powerPledgeWidget)
  }

  updatePowerPledgeWidget(
    urlName: string,
    powerPledgeWidget: Partial<PowerPledgeWidgetModel> & Pick<PowerPledgeWidgetModel, 'uuid'>,
  ): Observable<PowerPledgeWidgetModel> {
    const url = `auctions/${urlName}/power-pledge-widget/${powerPledgeWidget.uuid}/`
    return this.patch<PowerPledgeWidgetModel>(url, powerPledgeWidget)
  }

  deletePowerPledgeWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/power-pledge-widget/${uuid}/`
    return this.delete<void>(url)
  }

  // Notification Widgets
  getNotificationWidgets(urlName: string): Observable<NotificationWidgetModel[]> {
    return this.get<NotificationWidgetModel[]>(`auctions/${urlName}/notification-widgets/`)
  }

  createNotificationWidget(
    urlName: string,
    notificationWidget: Omit<NotificationWidgetModel, 'uuid'>,
  ): Observable<NotificationWidgetModel> {
    const url = `auctions/${urlName}/notification-widgets/`
    return this.post<NotificationWidgetModel>(url, notificationWidget)
  }

  updateNotificationWidget(
    urlName: string,
    notificationWidget: Partial<NotificationWidgetModel> & Pick<NotificationWidgetModel, 'uuid'>,
  ): Observable<NotificationWidgetModel> {
    const url = `auctions/${urlName}/notification-widgets/${notificationWidget.uuid}/`
    return this.patch<NotificationWidgetModel>(url, notificationWidget)
  }

  deleteNotificationWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/notification-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Item Table Widgets
  getItemTableWidgets(urlName: string): Observable<ItemTableWidgetModel[]> {
    return this.get<ItemTableWidgetModel[]>(`auctions/${urlName}/item-table-widgets/`)
  }

  createItemTableWidget(
    urlName: string,
    itemTableWidget: Omit<ItemTableWidgetModel, 'uuid'>,
  ): Observable<ItemTableWidgetModel> {
    const url = `auctions/${urlName}/item-table-widgets/`
    return this.post<ItemTableWidgetModel>(url, itemTableWidget)
  }

  updateItemTableWidget(
    urlName: string,
    itemTableWidget: Partial<ItemTableWidgetModel> & Pick<ItemTableWidgetModel, 'uuid'>,
  ): Observable<ItemTableWidgetModel> {
    const url = `auctions/${urlName}/item-table-widgets/${itemTableWidget.uuid}/`
    return this.patch<ItemTableWidgetModel>(url, itemTableWidget)
  }

  deleteItemTableWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/item-table-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Raffle Winners Widgets
  getRaffleWinnersWidgets(urlName: string): Observable<RaffleWinnersWidgetModel[]> {
    return this.get<RaffleWinnersWidgetModel[]>(`auctions/${urlName}/raffle-winners-widgets/`)
  }

  createRaffleWinnersWidget(
    urlName: string,
    raffleWinnersWidget: Omit<RaffleWinnersWidgetModel, 'uuid'>,
  ): Observable<RaffleWinnersWidgetModel> {
    const url = `auctions/${urlName}/raffle-winners-widgets/`
    return this.post<RaffleWinnersWidgetModel>(url, raffleWinnersWidget)
  }

  updateRaffleWinnersWidget(
    urlName: string,
    raffleWinnersWidget: Partial<RaffleWinnersWidgetModel> & Pick<RaffleWinnersWidgetModel, 'uuid'>,
  ): Observable<RaffleWinnersWidgetModel> {
    const url = `auctions/${urlName}/raffle-winners-widgets/${raffleWinnersWidget.uuid}/`
    return this.patch<RaffleWinnersWidgetModel>(url, raffleWinnersWidget)
  }

  deleteRaffleWinnersWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/raffle-winners-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Honour Roll Widgets
  getHonourRollWidgets(urlName: string): Observable<HonourRollWidgetModel[]> {
    return this.get<HonourRollWidgetModel[]>(`auctions/${urlName}/honour-roll-widget/`)
  }

  createHonourRollWidget(
    urlName: string,
    honourRollWidget: Omit<HonourRollWidgetModel, 'uuid'>,
  ): Observable<HonourRollWidgetModel> {
    const url = `auctions/${urlName}/honour-roll-widget/`
    return this.post<HonourRollWidgetModel>(url, honourRollWidget)
  }

  updateHonourRollWidget(
    urlName: string,
    honourRollWidget: Partial<HonourRollWidgetModel> & Pick<HonourRollWidgetModel, 'uuid'>,
  ): Observable<HonourRollWidgetModel> {
    const url = `auctions/${urlName}/honour-roll-widget/${honourRollWidget.uuid}/`
    return this.patch<HonourRollWidgetModel>(url, honourRollWidget)
  }

  deleteHonourRollWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/honour-roll-widget/${uuid}/`
    return this.delete<void>(url)
  }

  // Honour Roll Activities
  getHonourRollActivities(
    urlName: string,
    widget: string,
    limit = 10,
    offset = 0,
    ordering = '-created',
  ): Observable<GbPaginatedResponse<HonourRollActivity>> {
    return this.get<GbPaginatedResponse<HonourRollActivity>>(
      `auctions/${urlName}/honour-roll-activities/?widget=${widget}&limit=${limit}&offset=${offset}&ordering=${ordering}`,
    )
  }

  // Thermometer Widgets
  getThermometerWidgets(urlName: string): Observable<ThermometerWidgetModel[]> {
    return this.get<ThermometerWidgetModel[]>(`auctions/${urlName}/thermometer-widgets/`)
  }

  createThermometerWidget(
    urlName: string,
    thermometerWidget: Omit<ThermometerWidgetModel, 'uuid'>,
  ): Observable<ThermometerWidgetModel> {
    const url = `auctions/${urlName}/thermometer-widgets/`
    return this.post<ThermometerWidgetModel>(url, thermometerWidget)
  }

  updateThermometerWidget(
    urlName: string,
    thermometerWidget: Partial<ThermometerWidgetModel> & Pick<ThermometerWidgetModel, 'uuid'>,
  ): Observable<ThermometerWidgetModel> {
    const url = `auctions/${urlName}/thermometer-widgets/${thermometerWidget.uuid}/`
    return this.patch<ThermometerWidgetModel>(url, thermometerWidget)
  }

  deleteThermometerWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/thermometer-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Raffle Draw Widgets
  getRaffleDrawWidgets(urlName: string): Observable<RaffleDrawWidgetModel[]> {
    return this.get<RaffleDrawWidgetModel[]>(`auctions/${urlName}/raffle-draw-widgets/`)
  }

  createRaffleDrawWidget(
    urlName: string,
    raffleDrawWidget: Omit<RaffleDrawWidgetModel, 'uuid'>,
  ): Observable<RaffleDrawWidgetModel> {
    const url = `auctions/${urlName}/raffle-draw-widgets/`
    return this.post<RaffleDrawWidgetModel>(url, raffleDrawWidget)
  }

  updateRaffleDrawWidget(
    urlName: string,
    raffleDrawWidget: Partial<RaffleDrawWidgetModel> & Pick<RaffleDrawWidgetModel, 'uuid'>,
  ): Observable<RaffleDrawWidgetModel> {
    const url = `auctions/${urlName}/raffle-draw-widgets/${raffleDrawWidget.uuid}/`
    return this.patch<RaffleDrawWidgetModel>(url, raffleDrawWidget)
  }

  deleteRaffleDrawWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/raffle-draw-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // QR Code Widgets
  getQrCodeWidgets(urlName: string): Observable<QrCodeWidgetModel[]> {
    return this.get<QrCodeWidgetModel[]>(`auctions/${urlName}/qr-code-widgets/`)
  }

  createQrCodeWidget(urlName: string, qrCodeWidget: Omit<QrCodeWidgetModel, 'uuid'>): Observable<QrCodeWidgetModel> {
    const url = `auctions/${urlName}/qr-code-widgets/`
    return this.post<QrCodeWidgetModel>(url, qrCodeWidget)
  }

  updateQrCodeWidget(
    urlName: string,
    qrCodeWidget: Partial<QrCodeWidgetModel> & Pick<QrCodeWidgetModel, 'uuid'>,
  ): Observable<QrCodeWidgetModel> {
    const url = `auctions/${urlName}/qr-code-widgets/${qrCodeWidget.uuid}/`
    return this.patch<QrCodeWidgetModel>(url, qrCodeWidget)
  }

  deleteQrCodeWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/qr-code-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  // Video Widgets
  getVideoWidgets(urlName: string): Observable<VideoWidgetModel[]> {
    return this.get<VideoWidgetModel[]>(`auctions/${urlName}/video-widgets/`)
  }

  createVideoWidget(urlName: string, videoWidget: Omit<VideoWidgetModel, 'uuid'>): Observable<VideoWidgetModel> {
    const url = `auctions/${urlName}/video-widgets/`
    return this.post<VideoWidgetModel>(url, videoWidget)
  }

  updateVideoWidget(
    urlName: string,
    videoWidget: Partial<VideoWidgetModel> & Pick<VideoWidgetModel, 'uuid'>,
  ): Observable<VideoWidgetModel> {
    const url = `auctions/${urlName}/video-widgets/${videoWidget.uuid}/`
    return this.patch<VideoWidgetModel>(url, videoWidget)
  }

  deleteVideoWidget(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/video-widgets/${uuid}/`
    return this.delete<void>(url)
  }

  softDeleteWidget(urlName: string, widgetPath: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/${widgetPath}/${uuid}/soft-delete/`
    return this.post<void>(url, {})
  }

  restoreWidget<T>(urlName: string, widgetPath: string, uuid: string): Observable<T> {
    const url = `auctions/${urlName}/${widgetPath}/${uuid}/restore/`
    return this.post<T>(url, {})
  }
}
